import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, ArrowLeft, Wallet, RefreshCw } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { logger } from '../services/logger';
import { supabase } from '../lib/db';
import { useSettings } from '../lib/settings/settingsContext';

interface DepositDetails {
  merchantTransactionId?: string;
  amount?: number;
  currency?: string;
  peachTransactionId?: string;
}

export function DepositResult() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  
  const [loading, setLoading] = useState(false);
  const [walletBalance, setWalletBalance] = useState<number | null>(null);
  const [paymentProcessing, setPaymentProcessing] = useState(true);

  // Extract parameters from URL
  const status = searchParams.get('status') || 'unknown';
  const message = searchParams.get('message') || '';
  const detailsParam = searchParams.get('details');
  
  let details: DepositDetails = {};
  if (detailsParam) {
    try {
      details = JSON.parse(detailsParam);
    } catch (error) {
      console.error('Failed to parse details:', error);
    }
  }

  // Check for pending deposit in localStorage
  useEffect(() => {
    const pendingDeposit = localStorage.getItem('peach_pending_deposit');
    if (pendingDeposit) {
      try {
        const pending = JSON.parse(pendingDeposit);
        if (!details.merchantTransactionId) {
          details = { ...details, ...pending };
        }
        // Clean up localStorage
        localStorage.removeItem('peach_pending_deposit');
      } catch (error) {
        console.error('Failed to parse pending deposit:', error);
      }
    }
  }, []);

  // Fetch updated wallet balance using Supabase directly
  const fetchWalletBalance = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Check recent wallet_transactions to see if payment was processed
      const { data: transactionsData } = await supabase
        .from('wallet_transactions')
        .select('*')
        .eq('user_id', user.id)
        .eq('type', 'deposit')
        .order('created_at', { ascending: false })
        .limit(3);
      
      // Check if there's a very recent deposit (within last 5 minutes)
      const recentDeposit = transactionsData?.find(transaction => {
        const transactionTime = new Date(transaction.created_at).getTime();
        const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);
        return transactionTime > fiveMinutesAgo && 
               transaction.description?.includes('Peach Payments');
      });
      
      if (recentDeposit) {
        setPaymentProcessing(false); // Payment was processed recently
      }

      const { data: walletData, error: walletError } = await supabase
        .from('user_wallets')
        .select('balance')
        .eq('user_id', user.id)
        .single();

      if (walletError) {
        if (walletError.code === 'PGRST116') {
          // No wallet found, create one with zero balance
          console.log(`Creating new wallet for user: ${user.id}`);
          const { data: newWallet, error: createError } = await supabase
            .from('user_wallets')
            .insert({
              user_id: user.id,
              balance: 0
            })
            .select('balance')
            .single();

          if (createError) {
            console.error('Failed to create wallet:', createError);
            setWalletBalance(0);
          } else {
            setWalletBalance(newWallet?.balance || 0);
          }
        } else {
          console.error('Wallet lookup error:', walletError);
          setWalletBalance(0);
        }
      } else if (walletData) {
        const balanceValue = walletData.balance || 0;
        setWalletBalance(balanceValue);
        
        // If we have a balance, payment processing is complete
        if (balanceValue > 0) {
          setPaymentProcessing(false);
        }
      }
    } catch (error) {
      console.error('Failed to fetch wallet balance:', error);
      setWalletBalance(0); // Set to 0 instead of staying in loading state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (status === 'success') {
      fetchWalletBalance();
      
      // Set a timeout to stop showing "processing" after 30 seconds
      const timeout = setTimeout(() => {
        setPaymentProcessing(false);
        setWalletBalance(prev => prev === null ? 0 : prev); // Fallback to 0 if still loading after timeout
      }, 30000);

      return () => clearTimeout(timeout);
    }
  }, [status, user]);

  // Set up real-time subscription for wallet updates
  useEffect(() => {
    if (!user || status !== 'success') return;

    const setupRealtimeSubscription = async () => {
      try {
        // Subscribe to wallet changes using the existing supabase client
        const subscription = supabase
          .channel(`wallet-changes-${user.id}`)
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_wallets',
              filter: `user_id=eq.${user.id}`
            },
            (payload) => {
              console.log('Wallet update received in DepositResult:', payload);
              if (payload.new && typeof payload.new === 'object' && 'balance' in payload.new) {
                const newBalance = payload.new.balance as number;
                setWalletBalance(newBalance);
                setLoading(false); // Stop loading when real-time update arrives
                setPaymentProcessing(false); // Payment has been processed
              }
            }
          )
          .subscribe();

        return () => {
          subscription.unsubscribe();
        };
      } catch (error) {
        console.error('Failed to set up real-time subscription:', error);
      }
    };

    const cleanup = setupRealtimeSubscription();
    return () => {
      cleanup.then(cleanupFn => cleanupFn && cleanupFn());
    };
  }, [user, status]);

  // Log the result
  useEffect(() => {
    logger.info('Deposit result page loaded', {
      status,
      message,
      details,
      userId: user?.id
    });
  }, [status, message, details, user]);

  const getStatusIcon = () => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-16 h-16 text-green-500" />;
      case 'failed':
        return <XCircle className="w-16 h-16 text-red-500" />;
      case 'error':
        return <AlertCircle className="w-16 h-16 text-yellow-500" />;
      default:
        return <AlertCircle className="w-16 h-16 text-gray-500" />;
    }
  };

  const getStatusTitle = () => {
    switch (status) {
      case 'success':
        return 'Deposit Successful!';
      case 'failed':
        return 'Deposit Failed';
      case 'error':
        return 'Payment Error';
      default:
        return 'Payment Status Unknown';
    }
  };

  const getStatusMessage = () => {
    switch (status) {
      case 'success':
        return message || 'Your deposit has been processed successfully and your wallet has been credited.';
      case 'failed':
        return message || 'Your deposit could not be processed. Please try again or contact support.';
      case 'error':
        return message || 'There was an error processing your payment. Please contact support if this persists.';
      default:
        return 'We could not determine the status of your payment. Please check your wallet balance or contact support.';
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'failed':
        return 'text-red-600';
      case 'error':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const formatAmount = (cents?: number) => {
    if (!cents) return null;
    return `R${(cents / 100).toFixed(2)}`;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          {/* Status Icon */}
          <div className="flex justify-center mb-6">
            {getStatusIcon()}
          </div>

          {/* Title */}
          <h1 className={`text-2xl font-bold mb-4 ${getStatusColor()}`}>
            {getStatusTitle()}
          </h1>

          {/* Message */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {getStatusMessage()}
          </p>

          {/* Deposit Details */}
          {(details.amount || details.merchantTransactionId) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="font-semibold text-gray-800 mb-3">Transaction Details</h3>
              <div className="space-y-2 text-sm">
                {details.amount && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium">{formatAmount(details.amount)} {details.currency || 'ZAR'}</span>
                  </div>
                )}
                {details.merchantTransactionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reference:</span>
                    <span className="font-mono text-xs">{details.merchantTransactionId}</span>
                  </div>
                )}
                {details.peachTransactionId && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Payment ID:</span>
                    <span className="font-mono text-xs">{details.peachTransactionId}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Wallet Balance (for successful deposits) */}
          {status === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Wallet className="w-5 h-5 text-green-600" />
                  <span className="font-medium text-green-800">Wallet Balance:</span>
                </div>
                <div className="flex items-center space-x-2">
                  {loading || paymentProcessing ? (
                    <div className="flex items-center space-x-2">
                      <RefreshCw className="w-4 h-4 animate-spin text-green-600" />
                      <span className="text-green-700">
                        {paymentProcessing ? 'Processing payment...' : 'Loading...'}
                      </span>
                    </div>
                  ) : (
                    <>
                      <span className="font-bold text-green-800">
                        {walletBalance !== null ? `R${walletBalance.toFixed(2)}` : 'R0.00'}
                      </span>
                      <button
                        type="button"
                        onClick={fetchWalletBalance}
                        className="ml-2 p-1 text-green-600 hover:text-green-800"
                        title="Refresh balance"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>
              {paymentProcessing && (
                <div className="mt-2 text-sm text-green-700">
                  <p>⏳ Payment is being processed. This usually takes a few seconds.</p>
                  <p className="mt-1">If it takes longer than 30 seconds, click the refresh button above.</p>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3">
            {status === 'success' ? (
              <>
                <button
                  onClick={() => navigate('/markets')}
                  className="w-full bg-wayja-teal text-white font-semibold py-3 px-4 rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Start Trading
                </button>
                <button
                  onClick={() => navigate('/account/wallet')}
                  className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  View Wallet
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/account/wallet')}
                  className="w-full bg-wayja-teal text-white font-semibold py-3 px-4 rounded-lg hover:bg-opacity-90 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="w-full bg-gray-100 text-gray-700 font-medium py-3 px-4 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Contact Support
                </button>
              </>
            )}
            
            <button
              onClick={() => navigate('/')}
              className="w-full flex items-center justify-center space-x-2 text-gray-500 hover:text-gray-700 transition-colors py-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to Home</span>
            </button>
          </div>
        </div>

        {/* Additional Help */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Need help?{' '}
            <button
              onClick={() => navigate('/contact')}
              className="text-wayja-teal hover:underline font-medium"
            >
              Contact our support team
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
